import React, { useState, useEffect } from 'react';
import { useEntity } from '../contexts/EntityContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { Plus, Trash2, Edit2, Calendar as CalendarIcon, Users } from 'lucide-react';
import { format } from 'date-fns';
import { nl, enUS } from 'date-fns/locale';

import { API_URL } from '../config/api';

const COLORS = ['#2563EB', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

export function CapTable() {
  const { selectedEntity, entities, isDutchEntity } = useEntity();
  const { t, language } = useLanguage();
  const [shareholders, setShareholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingShareholder, setEditingShareholder] = useState(null);
  const [useEntityAsName, setUseEntityAsName] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    shares: '',
    share_class: 'common',
    percentage: '',
    vesting_start: null,
    vesting_months: '',
    cliff_months: '',
    notes: ''
  });

  const shareClasses = isDutchEntity && language === 'nl' ? [
    { value: 'common', label: 'Gewoon' },
    { value: 'preferred', label: 'Preferent' },
    { value: 'preferred_a', label: 'Preferent A' },
    { value: 'preferred_b', label: 'Preferent B' }
  ] : [
    { value: 'common', label: 'Common' },
    { value: 'preferred', label: 'Preferred' },
    { value: 'preferred_a', label: 'Series A' },
    { value: 'preferred_b', label: 'Series B' }
  ];

  useEffect(() => {
    if (selectedEntity) {
      fetchShareholders();
    }
  }, [selectedEntity]);

  const fetchShareholders = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/shareholders?entity_id=${selectedEntity.entity_id}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setShareholders(data);
      }
    } catch (error) {
      console.error('Failed to fetch shareholders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      entity_id: selectedEntity.entity_id,
      name: formData.name,
      email: formData.email || null,
      shares: parseInt(formData.shares),
      share_class: formData.share_class,
      percentage: parseFloat(formData.percentage),
      vesting_start: formData.vesting_start?.toISOString() || null,
      vesting_months: formData.vesting_months ? parseInt(formData.vesting_months) : null,
      cliff_months: formData.cliff_months ? parseInt(formData.cliff_months) : null,
      notes: formData.notes || null
    };

    try {
      const url = editingShareholder
        ? `${API_URL}/api/shareholders/${editingShareholder.shareholder_id}`
        : `${API_URL}/api/shareholders`;

      const response = await fetch(url, {
        method: editingShareholder ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        fetchShareholders();
        resetForm();
        setDialogOpen(false);
      }
    } catch (error) {
      console.error('Failed to save shareholder:', error);
    }
  };

  const handleDelete = async (shareholderId) => {
    if (!window.confirm(isDutchEntity && language === 'nl' 
      ? 'Weet je zeker dat je deze aandeelhouder wilt verwijderen?' 
      : 'Are you sure you want to delete this shareholder?')) {
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/api/shareholders/${shareholderId}`,
        { method: 'DELETE', credentials: 'include' }
      );
      if (response.ok) {
        fetchShareholders();
      }
    } catch (error) {
      console.error('Failed to delete shareholder:', error);
    }
  };

  const handleEdit = (sh) => {
    setEditingShareholder(sh);
    // Check if shareholder name matches any existing entity
    const isEntity = entities.some(e => e.name === sh.name && e.entity_id !== selectedEntity?.entity_id);
    setUseEntityAsName(isEntity);
    setFormData({
      name: sh.name,
      email: sh.email || '',
      shares: sh.shares.toString(),
      share_class: sh.share_class,
      percentage: sh.percentage.toString(),
      vesting_start: sh.vesting_start ? new Date(sh.vesting_start) : null,
      vesting_months: sh.vesting_months?.toString() || '',
      cliff_months: sh.cliff_months?.toString() || '',
      notes: sh.notes || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      shares: '',
      share_class: 'common',
      percentage: '',
      vesting_start: null,
      vesting_months: '',
      cliff_months: '',
      notes: ''
    });
    setEditingShareholder(null);
    setUseEntityAsName(false);
  };

  const totalShares = shareholders.reduce((sum, sh) => sum + sh.shares, 0);

  const chartData = shareholders.map(sh => ({
    name: sh.name,
    value: sh.percentage,
    shares: sh.shares
  }));

  const getShareClassLabel = (value) => {
    const found = shareClasses.find(c => c.value === value);
    return found?.label || value;
  };

  if (!selectedEntity) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">
          {isDutchEntity ? 'Selecteer eerst een entiteit' : 'Select an entity first'}
        </p>
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('nav.capTable')}
          </h1>
          <p className="text-muted-foreground">{selectedEntity.name}</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="btn-brand" data-testid="add-shareholder-btn">
              <Plus className="w-4 h-4 mr-2" />
              {isDutchEntity && language === 'nl' ? 'Aandeelhouder Toevoegen' : 'Add Shareholder'}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                {editingShareholder 
                  ? (isDutchEntity && language === 'nl' ? 'Aandeelhouder Bewerken' : 'Edit Shareholder')
                  : (isDutchEntity && language === 'nl' ? 'Aandeelhouder Toevoegen' : 'Add Shareholder')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>{isDutchEntity && language === 'nl' ? 'Naam' : 'Name'}</Label>
                {/* Toggle: pick from existing entities or type manually */}
                <div className="flex items-center gap-2 mb-2 mt-1">
                  <input
                    type="checkbox"
                    id="use-entity-toggle"
                    checked={useEntityAsName}
                    onChange={(e) => {
                      setUseEntityAsName(e.target.checked);
                      if (!e.target.checked) setFormData(f => ({ ...f, name: '' }));
                    }}
                    className="rounded"
                  />
                  <label htmlFor="use-entity-toggle" className="text-sm text-muted-foreground cursor-pointer">
                    {language === 'nl' ? 'Kies een bestaande BV als aandeelhouder' : 'Select an existing entity as shareholder'}
                  </label>
                </div>
                {useEntityAsName ? (
                  <Select
                    value={formData.name}
                    onValueChange={(value) => setFormData({ ...formData, name: value })}
                  >
                    <SelectTrigger data-testid="sh-entity-select">
                      <SelectValue placeholder={language === 'nl' ? 'Selecteer BV...' : 'Select entity...'} />
                    </SelectTrigger>
                    <SelectContent>
                      {entities
                        .filter(e => e.entity_id !== selectedEntity?.entity_id)
                        .map(e => (
                          <SelectItem key={e.entity_id} value={e.name}>{e.name}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    data-testid="sh-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder={language === 'nl' ? 'Naam aandeelhouder' : 'Shareholder name'}
                    required
                  />
                )}
              </div>

              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  data-testid="sh-email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('capTable.shares')}</Label>
                  <Input
                    type="number"
                    data-testid="sh-shares"
                    value={formData.shares}
                    onChange={(e) => setFormData({ ...formData, shares: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>{t('capTable.percentage')} (%)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    data-testid="sh-percentage"
                    value={formData.percentage}
                    onChange={(e) => setFormData({ ...formData, percentage: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <Label>{t('capTable.shareClass')}</Label>
                <Select
                  value={formData.share_class}
                  onValueChange={(value) => setFormData({ ...formData, share_class: value })}
                >
                  <SelectTrigger data-testid="sh-class">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {shareClasses.map(sc => (
                      <SelectItem key={sc.value} value={sc.value}>{sc.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>{t('capTable.vestingStart')}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.vesting_start 
                        ? format(formData.vesting_start, 'PPP', { locale: isDutchEntity && language === 'nl' ? nl : enUS })
                        : (isDutchEntity && language === 'nl' ? 'Selecteer datum' : 'Select date')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.vesting_start}
                      onSelect={(date) => setFormData({ ...formData, vesting_start: date })}
                      locale={isDutchEntity && language === 'nl' ? nl : enUS}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('capTable.vestingMonths')}</Label>
                  <Input
                    type="number"
                    data-testid="sh-vesting-months"
                    value={formData.vesting_months}
                    onChange={(e) => setFormData({ ...formData, vesting_months: e.target.value })}
                    placeholder="48"
                  />
                </div>
                <div>
                  <Label>{t('capTable.cliffMonths')}</Label>
                  <Input
                    type="number"
                    data-testid="sh-cliff-months"
                    value={formData.cliff_months}
                    onChange={(e) => setFormData({ ...formData, cliff_months: e.target.value })}
                    placeholder="12"
                  />
                </div>
              </div>

              <div>
                <Label>{isDutchEntity && language === 'nl' ? 'Opmerkingen' : 'Notes'}</Label>
                <Textarea
                  data-testid="sh-notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" className="flex-1" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" className="flex-1 btn-brand" data-testid="save-shareholder-btn">
                  {t('common.save')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary & Chart */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {isDutchEntity && language === 'nl' ? 'Eigendomsverdeling' : 'Ownership Distribution'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {shareholders.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, value }) => `${name} (${value.toFixed(1)}%)`}
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `${value.toFixed(2)}%`} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>{isDutchEntity && language === 'nl' ? 'Nog geen aandeelhouders' : 'No shareholders yet'}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {isDutchEntity && language === 'nl' ? 'Samenvatting' : 'Summary'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between py-2 border-b">
                <span className="text-muted-foreground">
                  {isDutchEntity && language === 'nl' ? 'Totaal Aandelen' : 'Total Shares'}
                </span>
                <span className="font-semibold">{totalShares.toLocaleString()}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-muted-foreground">
                  {isDutchEntity && language === 'nl' ? 'Aantal Aandeelhouders' : 'Number of Shareholders'}
                </span>
                <span className="font-semibold">{shareholders.length}</span>
              </div>
              <div className="space-y-2">
                {shareClasses.map(sc => {
                  const classShares = shareholders
                    .filter(sh => sh.share_class === sc.value)
                    .reduce((sum, sh) => sum + sh.shares, 0);
                  if (classShares === 0) return null;
                  return (
                    <div key={sc.value} className="flex justify-between text-sm">
                      <span>{sc.label}</span>
                      <span>{classShares.toLocaleString()} ({((classShares / totalShares) * 100).toFixed(1)}%)</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Shareholders List */}
      <Card>
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('capTable.shareholders')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 text-center">
              <div className="w-8 h-8 border-2 border-[hsl(var(--brand))] border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : shareholders.length > 0 ? (
            <div className="space-y-3">
              {shareholders.map((sh, idx) => (
                <div 
                  key={sh.shareholder_id}
                  className="shareholder-row"
                >
                  <div 
                    className="shareholder-avatar"
                    style={{ backgroundColor: `${COLORS[idx % COLORS.length]}20`, color: COLORS[idx % COLORS.length] }}
                  >
                    {sh.name.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{sh.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {sh.shares.toLocaleString()} {isDutchEntity && language === 'nl' ? 'aandelen' : 'shares'} • {getShareClassLabel(sh.share_class)}
                    </p>
                  </div>
                  <div className="text-right mr-4">
                    <p className="text-xl font-bold" style={{ color: COLORS[idx % COLORS.length] }}>
                      {sh.percentage.toFixed(2)}%
                    </p>
                    {sh.vesting_months && (
                      <p className="text-xs text-muted-foreground">
                        {sh.vesting_months}mo vesting
                      </p>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(sh)}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(sh.shareholder_id)}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-12 text-center">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {isDutchEntity && language === 'nl'
                  ? 'Nog geen aandeelhouders. Voeg je eerste aandeelhouder toe!'
                  : 'No shareholders yet. Add your first shareholder!'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
